import { commentBodyFromText, commentPlainText, commentSchema, normalizeCommentBody, normalizeCommentAnchor } from '@adornis/web-editor/comments.js';
import type { WebsiteBuilderNodeJSON } from '@adornis/web-editor/types.js';
import type { LandingLocale } from '../content/landingContent.js';
import { staticPageKeys, type StaticPageAudience, type StaticPageKey } from '../content/staticPageContent.js';

export type LandingEditorCommentPageKey = 'landing' | StaticPageKey;
export type LandingEditorCommentStatus = 'open' | 'resolved';

export interface LandingEditorCommentAnchor {
  pageKey: LandingEditorCommentPageKey;
  audience: StaticPageAudience;
  elementId: string;
  x: number;
  y: number;
  range?: { from: number; to: number; locale: string };
  documentRevision?: string;
}

export interface LandingEditorComment {
  id: string;
  text: string;
  body?: WebsiteBuilderNodeJSON;
  status: LandingEditorCommentStatus;
  anchor: LandingEditorCommentAnchor;
  createdLocale: LandingLocale;
  createdBy: string;
  createdAt: string;
  updatedBy: string;
  updatedAt: string;
  resolvedBy?: string;
  resolvedAt?: string;
  revision: number;
}

export interface LandingEditorCommentCreateInput {
  text: string;
  body?: WebsiteBuilderNodeJSON;
  anchor: LandingEditorCommentAnchor;
  createdLocale: LandingLocale;
}

export interface LandingEditorCommentUpdateInput {
  expectedRevision: number;
  text?: string;
  body?: WebsiteBuilderNodeJSON;
  status?: LandingEditorCommentStatus;
  anchor?: LandingEditorCommentAnchor;
}

export type LandingEditorCommentEvent =
  | { type: 'created' | 'updated'; comment: LandingEditorComment }
  | { type: 'deleted'; id: string };

const pageKeys = new Set<LandingEditorCommentPageKey>(['landing', ...staticPageKeys]);
const audiences = new Set<StaticPageAudience>(['applicant', 'company']);
const locales = new Set<LandingLocale>(['de', 'en', 'tr']);
const statuses = new Set<LandingEditorCommentStatus>(['open', 'resolved']);
const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;
const validAnchorId = /^[a-zA-Z0-9._:-]{1,240}$/u;

export const normalizeLandingEditorCommentAnchorIds = (value: unknown): Record<string, string> => {
  if (!isRecord(value)) return {};
  const anchorIds: Record<string, string> = {};
  for (const [elementId, anchorId] of Object.entries(value)) {
    if (validAnchorId.test(elementId) && typeof anchorId === 'string' && validAnchorId.test(anchorId)) anchorIds[elementId] = anchorId;
  }
  return anchorIds;
};

export const landingEditorCommentAnchorId = (anchorIds: Readonly<Record<string, string>>, elementId: string) =>
  anchorIds[elementId] ?? elementId;

export const landingEditorCommentElementId = (anchorIds: Readonly<Record<string, string>>, anchorId: string) =>
  Object.entries(anchorIds).find(([, value]) => value === anchorId)?.[0] ?? anchorId;

const normalizedCoordinate = (value: unknown): number | null => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return Math.min(1, Math.max(0, value));
};

export const normalizeLandingEditorCommentAnchor = (value: unknown): LandingEditorCommentAnchor | null => {
  if (!isRecord(value)) return null;
  const generic = normalizeCommentAnchor(value);
  if (!generic) return null;
  const x = normalizedCoordinate(value.x);
  const y = normalizedCoordinate(value.y);
  if (
    !pageKeys.has(value.pageKey as LandingEditorCommentPageKey) ||
    !audiences.has(value.audience as StaticPageAudience) ||
    typeof value.elementId !== 'string' ||
    !validAnchorId.test(value.elementId) ||
    x === null ||
    y === null
  )
    return null;
  return {
    pageKey: value.pageKey as LandingEditorCommentPageKey,
    audience: value.audience as StaticPageAudience,
    elementId: value.elementId,
    x,
    y,
    ...(generic.range ? { range: generic.range } : {}),
    ...(generic.documentRevision ? { documentRevision: generic.documentRevision } : {}),
  };
};

export const normalizeLandingEditorCommentText = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const text = value.replace(/\r\n?/gu, '\n').trim();
  return text.length > 0 && text.length <= 4_000 ? text : null;
};

export const normalizeLandingEditorCommentCreateInput = (
  value: unknown,
): LandingEditorCommentCreateInput | null => {
  if (!isRecord(value)) return null;
  const body = value.body === undefined ? undefined : normalizeCommentBody(value.body);
  if (body === null) return null;
  const text = normalizeLandingEditorCommentText(body ? commentPlainText(commentSchema.nodeFromJSON(body)) : value.text);
  const anchor = normalizeLandingEditorCommentAnchor(value.anchor);
  if (!text || !anchor || !locales.has(value.createdLocale as LandingLocale)) return null;
  return { text, ...(body ? { body } : {}), anchor, createdLocale: value.createdLocale as LandingLocale };
};

export const normalizeLandingEditorCommentUpdateInput = (
  value: unknown,
): LandingEditorCommentUpdateInput | null => {
  if (!isRecord(value) || !Number.isInteger(value.expectedRevision) || Number(value.expectedRevision) < 1)
    return null;
  const update: LandingEditorCommentUpdateInput = { expectedRevision: Number(value.expectedRevision) };
  if (value.text !== undefined || value.body !== undefined) {
    const body = value.body === undefined ? undefined : normalizeCommentBody(value.body);
    if (body === null) return null;
    const text = normalizeLandingEditorCommentText(body ? commentPlainText(commentSchema.nodeFromJSON(body)) : value.text);
    if (!text) return null;
    update.text = text;
    update.body = body ?? commentBodyFromText(text);
  }
  if (value.status !== undefined) {
    if (!statuses.has(value.status as LandingEditorCommentStatus)) return null;
    update.status = value.status as LandingEditorCommentStatus;
  }
  if (value.anchor !== undefined) {
    const anchor = normalizeLandingEditorCommentAnchor(value.anchor);
    if (!anchor) return null;
    update.anchor = anchor;
  }
  return update.text !== undefined || update.status !== undefined || update.anchor !== undefined ? update : null;
};
